import React, { useEffect, useState } from 'react'
import { Card, Button, Row, Col, Form, Container, Alert, InputGroup } from 'react-bootstrap'
import { useParams, useLocation, useHistory, Link } from 'react-router-dom'
import { successToast, warningToast } from '../../../constants/Toasts-Components'
import { formatCurrency } from '../../../constants/constant-func'
import { ContactsService, OpportunitiesService, InstituteService } from '../../../service/contacts-service'
import axios from 'axios'
import ReactLoading from "react-loading"
import Datepickers from '../../../components/Datepicker'
import { preventiviSpec } from '../../../constants/PreventiviSpec'
import { getLoggedInUser } from '../../../constants/localStorageFunctions'
import { BASE_URL } from "../../../constants/Endpoints"
import PreventiviResultsTable from './PreventiviResultsTable'

const ESTINZIONE_COEFFICIENTE = 0.88

const parseISODate = (value) => {
  if (!value) return null
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return null
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
}

// Completed months from the existing loan start to the reference date.
// The installment due on the reference date is still outstanding.
const monthsElapsed = (startDate, dataDecorrenza) => {
  const start = parseISODate(startDate)
  const end = parseISODate(dataDecorrenza)
  if (!start || !end) return null
  let months = (end.year - start.year) * 12 + (end.month - start.month)
  if (end.day <= start.day) months -= 1
  return months
}

// Remaining amount = rate residue * importo rata, with no 88% coefficient.
// Rate residue = durata - months between the existing loan start and data decorrenza.
const residuoAritmeticoPrestito = (prestito, dataDecorrenza) => {
  if (prestito.da_estinguere === 'off') return 0
  const rata = parseFloat(prestito.rata)
  const durata = parseInt(prestito.durata, 10)
  const elapsed = monthsElapsed(prestito.decorrenza, dataDecorrenza)
  if (rata > 0 && durata > 0 && elapsed != null) {
    const rateResidue = Math.min(durata, Math.max(0, durata - Math.max(0, elapsed)))
    return rateResidue * rata
  }
  return parseFloat(prestito.residuo) || 0
}

const isLoanToRenew = (prestito) => prestito && prestito.da_estinguere !== 'off'

const CurrencyInput = ({ value, onChange, name, step, readOnly, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);

  // Determine decimals based on step
  let decimals = 2;
  if (step == 1) decimals = 0;
  else if (step == 0.1) decimals = 1;

  const handleFocus = (e) => {
    if (!readOnly) setIsFocused(true);
    if (props.onFocus) props.onFocus(e);
  };
  
  const handleBlur = (e) => {
    setIsFocused(false);
    if (props.onBlur) props.onBlur(e);
  };

  const showRaw = isFocused && !readOnly;

  const displayValue = showRaw 
    ? value 
    : formatCurrency(value, decimals);

  return (
    <Form.Control
      type={showRaw ? "number" : "text"}
      name={name}
      value={displayValue || ''}
      onChange={onChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      step={step}
      readOnly={readOnly}
      {...props}
    />
  );
};

const Preventivi = () => {
  const params = useParams()
  const location = useLocation()
  const history = useHistory()
  console.log('All params:', params)
  
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [useDefaultValues, setUseDefaultValues] = useState(true)
  const [results, setResults] = useState(null)
  const [contactId, setContactId] = useState(null)
  const [contactData, setContactData] = useState(null)
  const [isContactFromUrl, setIsContactFromUrl] = useState(false)
  const [productId, setProductId] = useState(null)
  const [productData, setProductData] = useState(null)
  const [hasMatchedCombinations, setHasMatchedCombinations] = useState(false)
  const [preventiviId, setPreventiviId] = useState(null)
  const [filteredConvenzioni, setFilteredConvenzioni] = useState([])
  const [canForceEdit, setCanForceEdit] = useState(false)
  const [userConfig, setUserConfig] = useState(null)
  const [opportunityId, setOpportunityId] = useState(null)
  const [prestiti, setPrestiti] = useState([])
  const [maxDurataRataMapping, setMaxDurataRataMapping] = useState({})
  const [institutes, setInstitutes] = useState([])

  // Form state
  const [formData, setFormData] = useState({
    codiceQualifica: '',
    codiceTipoContratto: '',
    codiceTipoImpiego: '',
    convenzione: '',
    numeroRate: '120',
    importoRata: '',
    percentualeProvvigione: '',
    dataNascita: '',
    dataDecorrenza: '',
    dataAssunzione: '',
    sesso: 'M',
    stipendioNetto: '',
    rinnovoPrestitoPrecedente: 'N',
    matched_combinations: []
  })
  
  // Options from API
  const [options, setOptions] = useState({
    tipiQualifiche: [],
    tipiContratti: [],
    tipiImpiego: [],
    durata: []
  })

  // Fetch preventivatore options and criteri data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)

      // Fetch institutes (used to link preventivo rows to Opportunity.institute)
      try {
        const instituteService = new InstituteService()
        const institutesResponse = await instituteService.list()
        setInstitutes(institutesResponse.data || institutesResponse || [])
      } catch (error) {
        console.error('Error fetching institutes:', error)
      }

      // Fetch preventivatore options
      try {
        const optionsResponse = await axios({
          method: 'get',
          url: 'https://censimenti-csq.jazeeautomation.com/criteri_assuntivi/api/esegui',
          headers: {
            'x-api-key': process.env.REACT_APP_CENSIMENTI_API_KEY || ''
          }
        })
        console.log('Preventivatore Options Result:', optionsResponse.data)
        const responseData = optionsResponse.data
        setOptions({
          tipiQualifiche: responseData.preventitatore_options?.tipiQualifiche?.qualifiche || [],
          tipiContratti: responseData.preventitatore_options?.tipiContratti?.tipiContratto || [],
          tipiImpiego: responseData.preventitatore_options?.tipiImpiego?.tipiImpiego || [],
          durata: responseData.durata_options || []
        })
      } catch (error) {
        console.error('Error fetching options:', error)
        warningToast('Errore nel caricamento delle opzioni')
      }
      
            // Check for contact_id and product_id in URL params
      const searchParams = new URLSearchParams(location.search)
      const contactIdParam = searchParams.get('contact_id')
      const productIdParam = searchParams.get('product_id')
      const opportunityIdParam = searchParams.get('opportunity_id')
      
      // Fetch product data if product_id is provided
      if (productIdParam) {
        setProductId(productIdParam)
        try {
          const productResponse = await axios({
            method: 'get',
            url: `${BASE_URL}/api-v1/product/${productIdParam}/view`,
            withCredentials: true
          })
          
          if (productResponse.data) {
            const product = productResponse.data
            setProductData(product)
            console.log('Product data:', product)
            
            // Prefill form fields from product data
            setFormData(prev => ({
              ...prev,
              codiceTipoContratto: product.contratto || prev.codiceTipoContratto,
              codiceTipoImpiego: product.impiego || prev.codiceTipoImpiego
            }))
          }
        } catch (error) {
          console.error('Error fetching product:', error)
        }
      }

      // Fetch opportunity data if opportunity_id is provided
      if (opportunityIdParam) {
        setOpportunityId(opportunityIdParam)
        try {
          const oppService = new OpportunitiesService()
          const opp = await oppService.view(opportunityIdParam)
          console.log('Opportunity data:', opp)
          
          if (opp && opp.preventivi_request) {
             const request = opp.preventivi_request
             setFormData(prev => ({
                 ...prev,
                 ...request
             }))
             if (opp.product && opp.product.id) {
                 setProductId(opp.product.id)
             }
             successToast('Dati caricati dall\'opportunità')
          }
          else if (opp && opp.preventivi_request === null) {
            if (opp.product && opp.product.id) {
                 setProductId(opp.product.id)
                 setFormData(prev => ({
                     ...prev,
                     codiceTipoContratto: opp.product.contratto || prev.codiceTipoContratto,
                     codiceTipoImpiego: opp.product.impiego || prev.codiceTipoImpiego
                 }))
             }
          }
        } catch (error) {
          console.error('Error fetching opportunity:', error)
          warningToast('Errore nel caricamento dei dati dell\'opportunità')
        }
      }
      
      if (contactIdParam) {
        setContactId(contactIdParam)
        setIsContactFromUrl(true)
        
        // Fetch contact data
        try {
          const contactResponse = await axios({
            method: 'get',
            url: `${BASE_URL}/api-v1/people/contact/${contactIdParam}/view`,
            withCredentials: true
          })
          
          if (contactResponse.data) {
            const contact = contactResponse.data
            setContactData(contact)
            console.log('Contact data:', contact)
            
            // Prefill form fields from contact data
            setFormData(prev => ({
              ...prev,
              sesso: contact.sesso || 'M',
              stipendioNetto: contact.retribuzione || '',
              dataNascita: contact.data_di_nascita || contact.data_nascita || '',
              dataAssunzione: contact.data_di_assunzione || ''
            }))
            
            // Fetch latest criteri for this contact
            try {
              const criteriResponse = await axios({
                method: 'get',
                url: `${BASE_URL}/api-v1/people/criteri/${contactIdParam}/view-latest`,
                withCredentials: true
              })
              
              console.log('Latest Criteri Result:', criteriResponse.data)
              const criteriResult = criteriResponse.data
              console.log('Criteri Result:', criteriResult)
              console.log('Criteri Result Response:', criteriResult.response.matched_combinations)
              
              if (criteriResult && !criteriResult.error) {
                const requestBody = criteriResult.request || {}
                
                const extractedPrestiti = []
                let i = 1
                while (requestBody[`prestiti${i}_tipo`] !== undefined || requestBody[`prestiti${i}_rata`] !== undefined) {
                    extractedPrestiti.push({
                        da_estinguere: requestBody[`prestiti${i}_da_estinguere`] || '',
                        tipo: requestBody[`prestiti${i}_tipo`] || '',
                        rata: requestBody[`prestiti${i}_rata`] || '',
                        durata: requestBody[`prestiti${i}_durata`] || '',
                        residuo: requestBody[`prestiti${i}_residuo`] || '',
                        decorrenza: requestBody[`prestiti${i}_decorrenza`] || ''
                    })
                    i++
                }
                setPrestiti(extractedPrestiti)

                // analyze the criteri response and get the max rata
                // set import rata to the max rata in criteri response
                
                let mapping = {};
                if (criteriResult.response && criteriResult.response.max_durata_rata_mapping) {
                    try {
                        mapping = typeof criteriResult.response.max_durata_rata_mapping === 'string' 
                            ? JSON.parse(criteriResult.response.max_durata_rata_mapping)
                            : criteriResult.response.max_durata_rata_mapping;
                    } catch (e) {
                        console.error("Error parsing max_durata_rata_mapping", e);
                    }
                }
                setMaxDurataRataMapping(mapping);

                const loansToRenew = extractedPrestiti.filter(isLoanToRenew)
                setFormData(prev => ({
                  ...prev,
                  numeroRate: requestBody.durata || prev.numeroRate,
                  importoRata: (requestBody.durata && mapping[requestBody.durata]) ? mapping[requestBody.durata].toString() : prev.importoRata,
                  dataDecorrenza: requestBody.data_decorrenza || '',
                  matched_combinations: criteriResult.response.matched_combinations || [],
                  rinnovoPrestitoPrecedente: loansToRenew.length > 0 ? 'S' : prev.rinnovoPrestitoPrecedente
                }))
              }
            } catch (criteriError) {
              console.log('No criteri found for contact or error fetching:', criteriError)
              // It's okay if no criteri exists, continue with contact data only
            }

            if ((contact.trattenute || []).length > 0) {
              const mappedTrattenute = contact.trattenute.map(t => ({
                da_estinguere: '',
                tipo: t.tipo || '',
                rata: t.amount || '',
                durata: t.duration || '',
                residuo: t.residuo || '',
                decorrenza: t.start_date || ''
              }))
              setPrestiti(prev => (prev.length > 0 ? prev : mappedTrattenute))
              setFormData(prev => (
                prev.rinnovoPrestitoPrecedente === 'S'
                  ? prev
                  : { ...prev, rinnovoPrestitoPrecedente: 'S' }
              ))
            }
          }
        } catch (error) {
          console.error('Error fetching contact:', error)
          warningToast('Errore nel caricamento dei dati del contatto')
        }
      }
      
      setLoading(false)
    }
    
    loadData()
  }, [location.search])

  useEffect(() => {
    const config = getLoggedInUser()
    setUserConfig(config)
    if (config && config.can_force_edit) {
      setCanForceEdit(true)
    }
  }, [])

  useEffect(() => {
    if (formData.codiceTipoContratto && formData.codiceTipoImpiego && options.tipiContratti.length > 0 && options.tipiImpiego.length > 0) {
      const contrattoObj = options.tipiContratti.find(c => c.codiceTipoContratto == formData.codiceTipoContratto)
      const impiegoObj = options.tipiImpiego.find(i => i.codiceTipoImpiego == formData.codiceTipoImpiego)

      if (contrattoObj && impiegoObj) {
        const contrattoDesc = contrattoObj.descrizioneTipoContratto.toLowerCase()
        const impiegoDesc = impiegoObj.descrizioneTipoImpiego.toLowerCase()

        let specContratto = ''
        if (contrattoDesc.includes('cessione')) specContratto = 'cessione'
        else if (contrattoDesc.includes('delega')) specContratto = 'delega'

        let specImpiego = ''
        if (impiegoDesc.includes('pubblico')) specImpiego = 'pubblico'
        else if (impiegoDesc.includes('statale')) specImpiego = 'statale'
        else if (impiegoDesc.includes('privato')) specImpiego = 'privato'
        else if (impiegoDesc.includes('parapubblico')) specImpiego = 'parapubblico'

        const spec = preventiviSpec.find(s => s.contrato === specContratto && s.impiego === specImpiego)
        if (spec) {
          setFilteredConvenzioni(spec.convenzione)
        } else {
          setFilteredConvenzioni([])
        }
      }
    } else {
        setFilteredConvenzioni([])
    }
  }, [formData.codiceTipoContratto, formData.codiceTipoImpiego, options])

  const handleChange = (e) => {
    const { name, value } = e.target

    // Enforce 1 decimal place for importoRata and percentualeProvvigione
    if (name === 'importoRata' || name === 'percentualeProvvigione') {

        if (name === 'percentualeProvvigione' && userConfig) {
            const floatValue = parseFloat(value)
            if (userConfig.min_commission && floatValue < parseFloat(userConfig.min_commission)) {
                return userConfig.min_commission
            }
            if (userConfig.max_commission && floatValue > parseFloat(userConfig.max_commission)) {
                return userConfig.max_commission
            }
        }

      if (value.includes('.') && value.split('.')[1].length > 1) {
        return
      }
    }

    // Enforce 0 decimal places for stipendioNetto
    if (name === 'stipendioNetto') {
      if (value.includes('.')) {
        return
      }
    }

    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      }
      
      if (name === 'numeroRate' && maxDurataRataMapping && maxDurataRataMapping[value]) {
        newData.importoRata = maxDurataRataMapping[value].toString()
      }
      
      return newData
    })
  }

  const handleCheckboxChange = (e) => {
    setUseDefaultValues(e.target.checked)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate age
    if (formData.dataNascita) {
      const birthDate = new Date(formData.dataNascita)
      const today = new Date()
      let age = today.getFullYear() - birthDate.getFullYear()
      const m = today.getMonth() - birthDate.getMonth()
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--
      }

      if (age < 18) {
        warningToast('Il richiedente deve avere almeno 18 anni')
        return
      }
    }

    setSubmitting(true)
    
    try {
      // Build request body
      const requestBody = {
        codiceQualifica: formData.codiceQualifica,
        codiceTipoContratto: formData.codiceTipoContratto,
        codiceTipoImpiego: formData.codiceTipoImpiego,
        convenzione: formData.convenzione,
        dataAssunzione: formData.dataAssunzione,
        dataDecorrenza: formData.dataDecorrenza,
        dataNascita: formData.dataNascita,
        importoRata: parseFloat(formData.importoRata),
        numeroRate: parseInt(formData.numeroRate),
        percentualeProvvigione: parseFloat(formData.percentualeProvvigione),
        rinnovoPrestitoPrecedente: formData.rinnovoPrestitoPrecedente,
        sesso: formData.sesso,
        stipendioNetto: parseFloat(formData.stipendioNetto)
      }
      
      // Only include matched_combinations if it exists and has data
      if (formData.matched_combinations && formData.matched_combinations.length > 0) {
        requestBody.matched_combinations = formData.matched_combinations
        setHasMatchedCombinations(true)
      } else {
        setHasMatchedCombinations(false)
      }
      
      // Call external API
      const response = await axios({
        method: 'post',
        url: 'https://censimenti-csq.jazeeautomation.com/criteri_assuntivi/getpreventivi',
        headers: {
          'x-api-key': process.env.REACT_APP_CENSIMENTI_API_KEY || '',
          'Content-Type': 'application/json'
        },
        data: requestBody
      })
      
      console.log('Preventivi Response:', response.data)
      setResults(response.data)
      successToast('Richiesta inviata con successo!')
      
      // Save to backend
      try {
        const saveResponse = await axios({
          method: 'post',
          url: `${BASE_URL}/api-v1/people/preventivi/create`,
          headers: {
            'Content-Type': 'application/json'
          },
          data: {
            contact: contactId, // Link to contact if available
            request: requestBody,
            response: response.data
          },
          withCredentials: true
        })
        console.log('Preventivi saved to backend', saveResponse.data)
        if (saveResponse.data && saveResponse.data.id) {
          setPreventiviId(saveResponse.data.id)
        }
      } catch (saveError) {
        console.error('Error saving preventivi to backend:', saveError)
        // Don't fail the main request if backend save fails
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      const errors = error.response.data.errors
      for (const error in errors) {
        warningToast(errors[error])
      }
    } finally {
      setSubmitting(false)
    }
  }

  const matchInstituteForBanca = (banca) => {
    if (!banca || !institutes || institutes.length === 0) return null
    const target = banca.toString().trim().toLowerCase()
    const exact = institutes.find(inst =>
      (inst.name_in_preventivi || '').trim().toLowerCase() === target
    )
    if (exact) return exact
    // Fallback: partial match either direction
    return institutes.find(inst => {
      const nip = (inst.name_in_preventivi || '').trim().toLowerCase()
      if (!nip) return false
      return target.includes(nip) || nip.includes(target)
    }) || null
  }

  const handleCreateOpportunity = async (item) => {
    if (!contactId) {
      warningToast('Nessun contatto selezionato')
      return
    }

    try {
      const service = new ContactsService()
      const matchedInstitute = matchInstituteForBanca(item.banca)
      const opportunityData = {
        contact: contactId,
        product: productId || '',
        institute: matchedInstitute ? matchedInstitute.id : null,
        qualifica: formData.codiceQualifica,
        convenzione: formData.convenzione,
        importo_rata: item.importoRata,
        provvigione: formData.percentualeProvvigione,
        data_decorrenza: formData.dataDecorrenza,
        rinovo: formData.rinnovoPrestitoPrecedente,
        residuo: 0,
        banca: item.banca,
        assicurazione: item.assicurazione,
        capitale_lordo: item.capitaleLordo,
        netto_ricavo: item.nettoRicavo,
        TAN: item.TAN,
        TAEG: item.TAEG,
        TEG: item.TEG,
        totale_assicurazione: item.totaleAssicurazione,
        start_date: formData.dataDecorrenza,
        durata: formData.numeroRate,
        rata: item.importoRata,
        importo_richiesto: item.capitaleLordo,
        preventivi: preventiviId || null
      }

      if (opportunityId) {
        const oppService = new OpportunitiesService()
        await oppService.save(opportunityId, opportunityData)
        successToast('Opportunità aggiornata con successo!')
      } else {
        await service.createOpportunity(opportunityData)
        successToast('Opportunità creata con successo!')
      }
      history.push(`/contact-view/${contactId}`)
    } catch (error) {
      console.error('Error creating/updating opportunity:', error)
      warningToast('Errore durante il salvataggio dell\'opportunità')
    }
  }

  const residuoRimanente = prestiti.reduce(
    (acc, curr) => acc + (isLoanToRenew(curr) ? residuoAritmeticoPrestito(curr, formData.dataDecorrenza) : 0),
    0
  )
  const totalResiduoDaEstinguere = formData.rinnovoPrestitoPrecedente === 'S'
    ? residuoRimanente * ESTINZIONE_COEFFICIENTE
    : 0

  const isFieldDisabled = (fieldName) => {
    const restrictedFields = ['codiceTipoContratto', 'codiceTipoImpiego', 'sesso', 'rinnovoPrestitoPrecedente', 'stipendioNetto', 'dataNascita', 'dataDecorrenza', 'dataAssunzione']
    
    if (restrictedFields.includes(fieldName)) {
        return false
    }

    // Fields that are never disabled
    const alwaysEnabled = ['codiceQualifica', 'convenzione', 'numeroRate', 'importoRata']
    if (alwaysEnabled.includes(fieldName)) {
      return false
    }
    // Other fields are disabled when checkbox is unchecked
    return !useDefaultValues
  }

  if (loading) {
    return (
      <Container>
        <Row className="justify-content-center mt-5">
          <Col md={12} className="text-center">
            <ReactLoading type="spin" color="#007bff" height={50} width={50} />
            <p className="mt-3">Caricamento...</p>
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <Container fluid>
      <Row>
        <Col lg="12">
          <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
            <div>
              <h4 className="mb-3">Preventivi</h4>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb p-0 mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/">Dashboard</Link>
                  </li>
                  {contactId && (
                    <li className="breadcrumb-item">
                      <Link to={`/contact-view/${contactId}`}>
                        {contactData ? `${contactData.name} ${contactData.surname}` : 'Contatto'}
                      </Link>
                    </li>
                  )}
                  <li className="breadcrumb-item active" aria-current="page">Preventivi</li>
                </ol>
              </nav>
            </div>
            <Button variant="primary" className="btn-sm d-flex align-items-center" onClick={() => history.goBack()}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
              <span className="ml-2">Indietro</span>
            </Button>
          </div>
        </Col>
      </Row>
        {isContactFromUrl && contactData && (
        <Alert variant="info" className="mb-3">
            <i className="fas fa-info-circle me-2"></i>
            Dati precompilati dal contatto: <strong>{contactData.name} {contactData.surname}</strong>
        </Alert>
        )}
      <Row>
        {/* Left: Form */}
        <Col md={3} sm={12} className="mb-3">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">Preventivatore</h4>
              </div>
            </Card.Header>
            <Card.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              
              <Form onSubmit={handleSubmit}>
                <Row>
                  {/* Codice Qualifica */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="codiceQualifica" className="form-label">Codice Qualifica *</label>
                      <Form.Control
                        as="select"
                        name="codiceQualifica"
                        value={formData.codiceQualifica}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seleziona qualifica</option>
                        {options.tipiQualifiche.map((item, index) => (
                          <option key={index} value={item.codiceQualifiche}>
                            {item.descrizioneQualifiche}
                          </option>
                        ))}
                      </Form.Control>
                    </div>
                  </Col>

                  {/* Codice Tipo Contratto */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="codiceTipoContratto" className="form-label">Codice Tipo Contratto *</label>
                      <Form.Control
                        as="select"
                        name="codiceTipoContratto"
                        value={formData.codiceTipoContratto || productData?.contratto || ''}
                        onChange={handleChange}
                        required
                        disabled={isFieldDisabled('codiceTipoContratto')}
                      >
                        <option value="">Seleziona tipo contratto</option>
                        {options.tipiContratti.map((item, index) => (
                          <option key={index} value={item.codiceTipoContratto}>
                            {item.descrizioneTipoContratto}
                          </option>
                        ))}
                      </Form.Control>
                    </div>
                  </Col>

                  {/* Codice Tipo Impiego */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="codiceTipoImpiego" className="form-label">Codice Tipo Impiego *</label>
                      <Form.Control
                        as="select"
                        name="codiceTipoImpiego"
                        value={formData.codiceTipoImpiego || productData?.impiego || ''}
                        onChange={handleChange}
                        required
                        disabled={isFieldDisabled('codiceTipoImpiego')}
                      >
                        <option value="">Seleziona tipo impiego</option>
                        {options.tipiImpiego.map((item, index) => (
                          <option key={index} value={item.codiceTipoImpiego}>
                            {item.descrizioneTipoImpiego}
                          </option>
                        ))}
                      </Form.Control>
                    </div>
                  </Col>

                  {/* Convenzione */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="convenzione" className="form-label">Convenzione</label>
                      <Form.Control
                        as="select"
                        name="convenzione"
                        value={formData.convenzione}
                        onChange={handleChange}
                        disabled={isFieldDisabled('convenzione')}
                      >
                        <option value="">Seleziona convenzione</option>
                        {filteredConvenzioni.map((item, index) => (
                          <option key={index} value={item.codiceConvenzione}>
                            {item.descrizioneConvenzione}
                          </option>
                        ))}
                      </Form.Control>
                    </div>
                  </Col>

                  {/* Numero Rate */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="numeroRate" className="form-label">Numero Rate *</label>
                      <Form.Control
                        as="select"
                        name="numeroRate"
                        value={formData.numeroRate}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seleziona durata</option>
                        {options.durata.map((item, index) => (
                          <option key={index} value={item}>
                            {item}
                          </option>
                        ))}
                      </Form.Control>
                    </div>
                  </Col>

                  {/* Importo Rata */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="importoRata" className="form-label">Importo Rata *</label>
                      <CurrencyInput
                        name="importoRata"
                        value={formData.importoRata}
                        onChange={handleChange}
                        placeholder="Inserisci importo rata"
                        step="0.1"
                        required
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>
                  </Col>

                  {/* Percentuale Provvigione */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="percentualeProvvigione" className="form-label">Percentuale Provvigione *</label>
                      <InputGroup>
                        {userConfig?.min_commission && (
                          <InputGroup.Text className='px-1' >Min: {parseFloat(userConfig.min_commission).toFixed(1)}</InputGroup.Text>
                        )}
                        <Form.Control
                          type="number"
                          name="percentualeProvvigione"
                          value={formData.percentualeProvvigione}
                          onChange={handleChange}
                          placeholder="Inserisci percentuale provvigione"
                          step="0.1"
                          required
                          onWheel={(e) => e.target.blur()}
                        />
                        
                        {userConfig?.max_commission && (
                          <InputGroup.Text className='px-1'>Max: {parseFloat(userConfig.max_commission).toFixed(1)}</InputGroup.Text>
                        )}
                      </InputGroup>
                    </div>
                  </Col>

                  {/* Sesso */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="sesso" className="form-label">Sesso *</label>
                      <Form.Control
                        as="select"
                        name="sesso"
                        value={formData.sesso}
                        onChange={handleChange}
                        required
                        disabled={isFieldDisabled('sesso')}
                      >
                        <option value="M">M</option>
                        <option value="F">F</option>
                      </Form.Control>
                    </div>
                  </Col>

                  {/* Stipendio Netto */}
                  {/* Stipendio Netto */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="stipendioNetto" className="form-label">Stipendio Netto *</label>
                      <CurrencyInput
                        name="stipendioNetto"
                        value={formData.stipendioNetto}
                        onChange={handleChange}
                        placeholder="Inserisci stipendio netto"
                        step="1"
                        required
                        disabled={isFieldDisabled('stipendioNetto')}
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>
                  </Col>

                  {/* Rinnovo Prestito Precedente */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="rinnovoPrestitoPrecedente" className="form-label">Rinnovo Prestito Precedente *</label>
                      <Form.Control
                        as="select"
                        name="rinnovoPrestitoPrecedente"
                        value={formData.rinnovoPrestitoPrecedente}
                        onChange={handleChange}
                        disabled={isFieldDisabled('rinnovoPrestitoPrecedente')}
                        required
                      >
                        <option value="N">No</option>
                        <option value="S">Si</option>
                      </Form.Control>
                      {formData.rinnovoPrestitoPrecedente === 'S' && (
                        <div className="text-muted mt-1">
                          Residuo rimanente: {formatCurrency(residuoRimanente, 0)}
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* Data Nascita */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="dataNascita" className="form-label">Data Nascita *</label>
                      <Form.Control
                        type="date"
                        name="dataNascita"
                        value={formData.dataNascita}
                        onChange={handleChange}
                        required
                        disabled={isFieldDisabled('dataNascita')}
                      />
                    </div>
                  </Col>

                  {/* Data Decorrenza */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="dataDecorrenza" className="form-label">Data Decorrenza *</label>
                      <Form.Control
                        type="date"
                        name="dataDecorrenza"
                        value={formData.dataDecorrenza}
                        onChange={handleChange}
                        required
                        disabled={isFieldDisabled('dataDecorrenza')}
                      />
                    </div>
                  </Col>

                  {/* Data Assunzione */}
                  <Col md={12} className="mb-3">
                    <div>
                      <label htmlFor="dataAssunzione" className="form-label">Data Assunzione *</label>
                      <Form.Control
                        type="date"
                        name="dataAssunzione"
                        value={formData.dataAssunzione}
                        onChange={handleChange}
                        required
                        disabled={isFieldDisabled('dataAssunzione')}
                      />
                    </div>
                  </Col>
                </Row>

                {/* Submit Button */}
                <Row>
                  <Col md={12}>
                    <Button 
                      type="submit" 
                      variant="primary"
                      disabled={submitting}
                      className="mt-3"
                    >
                      {submitting ? (
                        <>
                          <ReactLoading 
                            type="spin" 
                            color="#fff" 
                            height={20} 
                            width={20} 
                            className="d-inline-block me-2"
                          />
                          Invio in corso...
                        </>
                      ) : (
                        'Invia Richiesta'
                      )}
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Right: Results (keeps rendering when results exist; empty card when none) */}
        <Col md={9} sm={12} className="mb-3">
          <Card>
            <Card.Header>
              <div className="header-title">
                <h4 className="card-title">Risultati Preventivi</h4>
              </div>
            </Card.Header>
            <Card.Body style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {results ? (
                <PreventiviResultsTable
                  results={results}
                  percentualeProvvigione={formData.percentualeProvvigione}
                  totalResiduoDaEstinguere={totalResiduoDaEstinguere}
                  showNettoCliente={true}
                  showActions={hasMatchedCombinations}
                  actionLabel={opportunityId ? 'Aggiorna Opportunità' : 'Crea Opportunità'}
                  onAction={handleCreateOpportunity}
                />
              ) : (
                <div className="text-center text-muted">Nessun risultato. Invia una richiesta per vedere i preventivi qui.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default Preventivi
